import React from 'react';
import { Box, IconButton, Stack, Typography, alpha, useTheme } from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';
import {
  CheckCircle as SuccessIcon,
  Close as CloseIcon,
  Error as ErrorIcon,
  Info as InfoIcon,
  WarningAmber as WarningIcon,
} from '@mui/icons-material';
import { borderRadius, componentSizes, getSemanticSurfaceTokens, spacing } from '../../../theme/theme';
import type { SemanticSurfaceTokens } from '../../../theme/theme';

export type WorkspaceStatusBannerTone = 'success' | 'info' | 'warning' | 'error';

export interface WorkspaceStatusBannerProps {
  tone: WorkspaceStatusBannerTone;
  children: React.ReactNode;
  icon?: React.ReactNode;
  action?: React.ReactNode;
  onClose?: () => void;
  dataTestId?: string;
  sx?: SxProps<Theme>;
}

const toneIcon: Record<WorkspaceStatusBannerTone, typeof InfoIcon> = {
  success: SuccessIcon,
  info: InfoIcon,
  warning: WarningIcon,
  error: ErrorIcon,
};

const toneTokens = (
  tokens: SemanticSurfaceTokens,
  tone: WorkspaceStatusBannerTone
): { bg: string; border: string; text: string } => {
  switch (tone) {
    case 'success':
      return tokens.status.success;
    case 'warning':
      return tokens.status.warning;
    case 'error':
      return tokens.status.error;
    case 'info':
    default:
      return tokens.status.info;
  }
};

/**
 * Compact, full-width workspace status strip.
 *
 * Use for inline success/info/warning/error messages inside a workspace tab
 * where a standard MUI Alert would be too heavy or would clash with the dense
 * workspace chrome styling.
 */
export const WorkspaceStatusBanner: React.FC<WorkspaceStatusBannerProps> = ({
  tone,
  children,
  icon,
  action,
  onClose,
  dataTestId,
  sx,
}) => {
  const theme = useTheme();
  const tokens = getSemanticSurfaceTokens(theme);
  const colors = toneTokens(tokens, tone);
  const DefaultIcon = toneIcon[tone];

  return (
    <Box
      data-testid={dataTestId}
      sx={[
        {
          display: 'flex',
          alignItems: 'center',
          gap: spacing.sm / 8,
          px: 0.75,
          py: 0.35,
          minHeight: componentSizes.toolbarCompact,
          borderRadius: `${borderRadius.none}px`,
          borderTop: `1px solid ${colors.border}`,
          borderBottom: `1px solid ${colors.border}`,
          bgcolor: colors.bg,
          color: colors.text,
        },
        ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
      ]}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
        {icon ?? <DefaultIcon sx={{ fontSize: componentSizes.icon }} />}
      </Box>
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        sx={{ flex: 1, minWidth: 0, fontSize: '0.68rem', fontWeight: 700, lineHeight: 1.25 }}
      >
        {typeof children === 'string' || typeof children === 'number' ? (
          <Typography variant="caption" sx={{ fontSize: 'inherit', fontWeight: 'inherit', color: 'inherit' }}>
            {children}
          </Typography>
        ) : (
          children
        )}
      </Stack>
      {action ? <Box sx={{ flexShrink: 0 }}>{action}</Box> : null}
      {onClose ? (
        <IconButton
          size="small"
          onClick={onClose}
          aria-label="Dismiss"
          sx={{
            width: 20,
            height: 20,
            p: 0.25,
            flexShrink: 0,
            color: 'inherit',
            borderRadius: `${borderRadius.sm}px`,
            opacity: 0.8,
            '&:hover': {
              bgcolor: alpha(theme.palette.common.black, 0.06),
              opacity: 1,
            },
          }}
        >
          <CloseIcon sx={{ fontSize: '0.75rem' }} />
        </IconButton>
      ) : null}
    </Box>
  );
};

export default WorkspaceStatusBanner;
